import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { ArrowLeft, ArrowUp, CalendarClock, CalendarDays, Check, Copy, History, MessageSquare, Pencil, RotateCcw, Search, Send, Sigma, SquarePen, Sparkles, Trash2, } from "lucide-react";
import AppButton from "../../../common_components/AppButton";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import AiRichText from "../../../common_components/AiSuggestions/AiRichText";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import { selectToken, selectUser } from "../../../redux/authSlice";
import CommonLoader from "../../../common_components/CommonLoader";
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
import { Dropdown, TextInput } from "../../../common_components/FormInputs";
import { activeConversationKey, readActiveConversation, writeActiveConversation } from "../askAiConversation";
import { toast } from "../../../common_components/Toast/ToastUtils";
import { reportBuilderChatService, sendReportBuilderMessage, } from "../../../services/ai.service";
import { fmtDateRange } from "../createReport.utils";
import { AFFILIATE_NETWORK_KEYS, getNetwork } from "../reportBuilder.networks";
import { GroupIcon } from "./builderIcons";
const BRAND = "#FF008E";
const BRAND_LIGHT = "#FF5CB8";
const BRAND_GRADIENT = `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`;
const REDUCED = "@media (prefers-reduced-motion: reduce)";
/** API network key → builder key, where the two differ. */
const API_TO_BUILDER = { ga: "google_analytics" };
const BUILDER_TO_API = { google_analytics: "ga" };
export const toBuilderNetworkKey = (api) => API_TO_BUILDER[api] || api;
export const toApiNetworkKey = (key) => BUILDER_TO_API[key] || key;
const DIMENSION_LABELS = {
    day: "Day",
    week: "Week",
    month: "Month",
    campaign: "Campaign",
    age: "Age",
    session_source_transaction_id: "Session Source / Transaction Id",
};
const THINK_STEPS = ["Reading your request", "Matching it to the connected networks", "Picking the columns"];
const KEYFRAMES = {
    "@keyframes askAiRise": {
        from: { opacity: 0, transform: "translateY(8px)" },
        to: { opacity: 1, transform: "translateY(0)" },
    },
    "@keyframes askAiPop": {
        "0%": { opacity: 0, transform: "scale(0.85)" },
        "70%": { opacity: 1, transform: "scale(1.04)" },
        "100%": { opacity: 1, transform: "scale(1)" },
    },
    "@keyframes askAiHalo": {
        "0%, 100%": { transform: "scale(0.85)", opacity: 0.1 },
        "50%": { transform: "scale(1.2)", opacity: 0.3 },
    },
    "@keyframes askAiSpin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    "@keyframes askAiTwinkle": {
        "0%, 100%": { transform: "scale(1) rotate(0deg)" },
        "50%": { transform: "scale(1.15) rotate(12deg)" },
    },
    "@keyframes askAiDot": {
        "0%, 80%, 100%": { opacity: 0.25, transform: "translateY(0)" },
        "40%": { opacity: 1, transform: "translateY(-3px)" },
    },
    "@keyframes askAiCaret": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0 } },
    "@keyframes askAiSweep": {
        from: { transform: "translateX(-120%) skewX(-20deg)" },
        to: { transform: "translateX(220%) skewX(-20deg)" },
    },
    "@keyframes askAiGlow": { "0%, 100%": { opacity: 0.35 }, "50%": { opacity: 0.7 } },
};
const rise = (index = 0, ms = 60) => ({
    animation: "askAiRise 360ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
    animationDelay: `${index * ms}ms`,
    [REDUCED]: { animation: "none" },
});
const pop = (index = 0, ms = 40) => ({
    animation: "askAiPop 300ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
    animationDelay: `${index * ms}ms`,
    [REDUCED]: { animation: "none" },
});
/** Starter prompts, built from the networks the client actually has. */
const startersFor = (networks) => {
    const keys = new Set(networks.map((n) => n.key));
    const out = [];
    if (keys.has("meta"))
        out.push("Meta spend, ROAS and purchases by campaign");
    if (keys.has("google_ads"))
        out.push("Weekly Google Ads cost, clicks and conversions");
    if (keys.has("shopify"))
        out.push("Monthly Shopify orders and revenue");
    if (keys.has("email"))
        out.push("Klaviyo campaign performance by week");
    // Affiliate networks get their own starters, kept even when the paid ones
    // already fill the list.
    const affiliates = networks.filter((n) => AFFILIATE_NETWORK_KEYS.includes(n.key));
    const affiliate = [];
    if (affiliates.length > 0)
        affiliate.push(`${affiliates[0].label} revenue, spend and ROAS by publisher`);
    if (affiliates.length > 1)
        affiliate.push("Revenue and spend of all affiliate networks by month");
    const picked = [...out.slice(0, affiliate.length ? 2 : 4), ...affiliate];
    if (keys.size > 1 && picked.length < 4)
        picked.push("Overview of all networks by month");
    return picked.slice(0, 4);
};
const uid = () => Math.random().toString(36).slice(2, 10);
// ── Conversations (stored server-side in ai_chats) ───────────────────────
// The last open chat is remembered per user AND per report (askAiConversation.ts).
const readActive = readActiveConversation;
const writeActive = writeActiveConversation;
/** Stored message → chat message. */
const fromStored = (m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    plan: m.plan,
    options: m.options,
    applied: m.applied,
    is_refusal: m.is_refusal,
    saved: true,
    action: m.action ?? null,
});
/**
 * Best match for a client the user named ("acme", "Acme Corp") among the ones
 * they may clone to. Exact, then prefix, then contains; '' when nothing fits,
 * so the card asks them to pick rather than guessing.
 */
const matchClient = (name, options) => {
    const want = name.trim().toLowerCase();
    if (!want)
        return "";
    const label = (o) => String(o.label || "").toLowerCase();
    const hit = options.find((o) => label(o) === want) ||
        options.find((o) => label(o).startsWith(want)) ||
        options.find((o) => label(o).includes(want)) ||
        options.find((o) => want.includes(label(o)) && label(o).length > 2);
    return hit ? String(hit.value) : "";
};
/** "just now", "5m ago", "3h ago", "Yesterday", "Sep 12". */
const relativeTime = (iso) => {
    if (!iso)
        return "";
    const then = new Date(iso);
    const mins = Math.floor((Date.now() - then.getTime()) / 60000);
    if (mins < 1)
        return "just now";
    if (mins < 60)
        return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24)
        return `${hours}h ago`;
    if (hours < 48)
        return "Yesterday";
    return then.toLocaleDateString(undefined, { month: "short", day: "numeric", year: then.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
};
/** History list sections, like a chat app sidebar. */
const sectionOf = (iso) => {
    if (!iso)
        return "Older";
    const hours = (Date.now() - new Date(iso).getTime()) / 3600000;
    if (hours < 24)
        return "Today";
    if (hours < 48)
        return "Yesterday";
    if (hours < 24 * 7)
        return "Previous 7 days";
    if (hours < 24 * 30)
        return "Previous 30 days";
    return "Older";
};
// ── Small pieces ─────────────────────────────────────────────────────────
const AiAvatar = ({ size = 28, busy = false, done = false }) => (_jsxs(Box, { sx: { position: "relative", width: size, height: size, flexShrink: 0 }, children: [busy && (_jsxs(_Fragment, { children: [_jsx(Box, { sx: { position: "absolute", inset: -5, borderRadius: "50%", bgcolor: BRAND, animation: "askAiHalo 1.8s ease-in-out infinite", [REDUCED]: { animation: "none", opacity: 0.12 } } }), _jsx(Box, { sx: {
                        position: "absolute",
                        inset: -3,
                        borderRadius: "50%",
                        background: `conic-gradient(from 0deg, ${BRAND}, ${BRAND_LIGHT}, #FF8AD0, ${BRAND})`,
                        animation: "askAiSpin 2.2s linear infinite",
                        [REDUCED]: { animation: "none" },
                        WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2px))",
                        mask: "radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2px))",
                    } })] })), _jsx(Box, { sx: {
                position: "relative",
                width: size,
                height: size,
                borderRadius: "50%",
                background: done ? "linear-gradient(135deg, #12B76A 0%, #32D583 100%)" : BRAND_GRADIENT,
                boxShadow: "0 4px 12px rgba(255,0,142,0.28)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background .4s ease",
            }, children: done ? (_jsx(Check, { size: Math.round(size * 0.52), color: "#fff", strokeWidth: 2.5 })) : (_jsx(Box, { sx: { display: "flex", animation: busy ? "none" : "askAiTwinkle 2.6s ease-in-out infinite", [REDUCED]: { animation: "none" } }, children: _jsx(Sparkles, { size: Math.round(size * 0.5), color: "#fff" }) })) })] }));
const Chip = ({ icon, color, index, children }) => (_jsxs(Box, { sx: {
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        height: 24,
        px: 1,
        borderRadius: "6px",
        border: "1px solid #E4E7EC",
        bgcolor: "#fff",
        fontSize: 12,
        fontWeight: 500,
        color: color || "#344054",
        whiteSpace: "nowrap",
        ...(index === undefined ? {} : pop(index)),
    }, children: [icon, children] }));
const PlanRow = ({ label, index, children }) => (_jsxs(Box, { sx: { display: "flex", gap: 1.25, alignItems: "flex-start", ...rise(index, 70) }, children: [_jsx(Typography, { sx: { width: 76, flexShrink: 0, fontSize: 11.5, fontWeight: 600, color: "#667085", pt: "4px" }, children: label }), _jsx(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 0.6, minWidth: 0 }, children: children })] }));
/** Tap-able follow-up / starter. */
const OptionChip = ({ label, index, disabled, onPick }) => (_jsxs(Box, { role: "button", tabIndex: disabled ? -1 : 0, "aria-disabled": disabled, onClick: () => !disabled && onPick(label), onKeyDown: (e) => {
        if (!disabled && (e.key === "Enter" || e.key === " "))
            onPick(label);
    }, sx: {
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        fontSize: 12.5,
        color: disabled ? "#98A2B3" : "#E6007A",
        border: "1px solid",
        borderColor: disabled ? "#EAECF0" : "#FFC8E9",
        borderRadius: "999px",
        pl: 1.1,
        pr: 1.4,
        py: 0.5,
        cursor: disabled ? "default" : "pointer",
        bgcolor: disabled ? "#FCFCFD" : "#FFFAFD",
        transition: "all .18s cubic-bezier(0.2,0.8,0.2,1)",
        "& svg": { transition: "transform .18s ease" },
        "&:hover": disabled
            ? undefined
            : { borderColor: BRAND, bgcolor: "#FFF3FA", transform: "translateY(-1px)", boxShadow: "0 6px 14px -8px rgba(255,0,142,0.6)", "& svg": { transform: "rotate(15deg)" } },
        ...rise(index, 60),
    }, children: [_jsx(Sparkles, { size: 11 }), label] }));
// ── Dialog ───────────────────────────────────────────────────────────────
// ── Action card (Clone / Send / Schedule) ────────────────────────────────
const SCHEDULE_WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SCHEDULE_PERIODS = {
    rtd: "Report to date",
    mtd: "Month to date",
    decremental: "Date adjustment",
};
const joinAnd = (items) => items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
/** "Every Monday and Friday at 9:00 AM Eastern" - the Schedule modal's wording. */
const scheduleWhen = (s) => {
    const [h] = s.time.split(":").map(Number);
    const at = `${h % 12 || 12}:00 ${h < 12 ? "AM" : "PM"} Eastern`;
    const days = joinAnd(s.weekdays.map((d) => SCHEDULE_WEEKDAYS[d]));
    if (s.frequency === "day")
        return `Every day at ${at}`;
    if (s.frequency === "week")
        return `Every ${days} at ${at}`;
    if (s.frequency === "bi_weekly")
        return `Every other ${days} at ${at}`;
    return `On the ${joinAnd(s.month_days.map(ordinal))} of every month at ${at}`;
};
const RECIPIENT_KIND = {
    internal: "Internal",
    contact: "External",
    external: "Additional External",
};
/**
 * The agent's proposed Clone or Send, as a card the user confirms. The card IS
 * the confirmation: nothing runs until its button is clicked, because Send
 * emails the client and Clone creates a report. Clone's name and client stay
 * editable, prefilled from what the user asked for.
 */
const ActionCard = ({ action, done, isLatest, actions, busy, onRun }) => {
    const ctx = actions?.context;
    const options = actions?.clientOptions || [];
    const requestedClient = action.client_name ? matchClient(action.client_name, options) : "";
    const [name, setName] = useState(action.report_name || (ctx ? `${ctx.report_name.trim()} (Copy)` : ""));
    const [cloneClientId, setCloneClientId] = useState(action.client_name ? requestedClient : actions?.clientId || "");
    const [nameError, setNameError] = useState("");
    const [networkCheck, setNetworkCheck] = useState(null);
    const isClone = action.type === "clone";
    // Same warning as the Clone dialog, following the picked client.
    useEffect(() => {
        setNetworkCheck(null);
        if (!isClone || !cloneClientId || !actions?.context.saved)
            return;
        let cancelled = false;
        actions.onCheckClone(cloneClientId).then((result) => {
            if (!cancelled)
                setNetworkCheck(result);
        });
        return () => {
            cancelled = true;
        };
        // onCheckClone is rebuilt every render; the client drives this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClone, cloneClientId]);
    const schedule = action.type === "schedule" ? action.schedule ?? null : null;
    const isSchedule = action.type === "schedule";
    const running = isClone ? Boolean(actions?.cloning) : isSchedule ? Boolean(actions?.scheduling) : Boolean(actions?.sending);
    const unavailable = !actions ||
        !ctx?.saved ||
        (isClone ? !ctx.can_clone : isSchedule ? !ctx.can_schedule || !schedule : !ctx.can_send);
    const cloneClientName = options.find((o) => String(o.value) === cloneClientId)?.label || "";
    const turningOff = Boolean(schedule && !schedule.enabled);
    const title = isClone ? "Clone report" : isSchedule ? (turningOff ? "Turn off schedule" : "Auto schedule") : "Send report";
    const buttonLabel = isClone
        ? "Clone & edit"
        : isSchedule
            ? turningOff
                ? "Turn off schedule"
                : schedule?.replaces_existing
                    ? "Update schedule"
                    : "Save schedule"
            : "Send Report";
    const doneLabel = isClone ? "Cloned" : isSchedule ? (turningOff ? "Turned off" : "Scheduled") : "Sent";
    const icon = (size) => isClone ? _jsx(Copy, { size: size }) : isSchedule ? _jsx(CalendarClock, { size: size }) : _jsx(Send, { size: size });
    const run = () => {
        if (!actions)
            return;
        if (isSchedule) {
            if (schedule)
                onRun(() => actions.onSchedule(schedule));
            return;
        }
        if (isClone) {
            if (!name.trim() || !cloneClientId) {
                setNameError(name.trim() ? "" : "Report name is required.");
                return;
            }
            onRun(async () => {
                const result = await actions.onClone({ client_id: cloneClientId, client_name: String(cloneClientName), report_name: name.trim() });
                if (result.nameError)
                    setNameError(result.nameError);
                return result.ok;
            });
        }
        else {
            onRun(actions.onSend);
        }
    };
    return (_jsxs(Box, { sx: {
            position: "relative",
            bgcolor: "#FCFCFD",
            border: "1px solid",
            borderColor: done ? "#A6F4C5" : "#EAECF0",
            borderRadius: "12px",
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            boxShadow: "0 8px 24px -16px rgba(16,24,40,0.25)",
            ...rise(1, 80),
            "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 14,
                right: 14,
                height: 2,
                borderRadius: 1,
                background: done ? "linear-gradient(135deg, #12B76A, #32D583)" : BRAND_GRADIENT,
            },
        }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Box, { sx: { width: 28, height: 28, borderRadius: "8px", bgcolor: "#FFE4F3", color: BRAND, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }, children: icon(15) }), _jsx(Typography, { sx: { fontSize: 13.5, fontWeight: 600, color: "#101828" }, children: title })] }), isSchedule ? (schedule ? (turningOff ? (_jsxs(Typography, { sx: { fontSize: 12.5, color: "#475467", lineHeight: "18px" }, children: ["\"", ctx?.report_name || "This report", "\" ", done ? "is" : "will be", " no longer sent automatically. The schedule's settings are kept, so it can be turned back on later."] })) : (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.75 }, children: [[
                        { label: "When", value: scheduleWhen(schedule) },
                        { label: "Period", value: SCHEDULE_PERIODS[schedule.period] },
                    ].map((row) => (_jsxs(Box, { sx: { display: "flex", gap: 1, fontSize: 12.5 }, children: [_jsx(Box, { component: "span", sx: { width: 70, flexShrink: 0, color: "#667085" }, children: row.label }), _jsx(Box, { component: "span", sx: { color: "#101828", fontWeight: 500 }, children: row.value })] }, row.label))), _jsxs(Box, { sx: { display: "flex", gap: 1, fontSize: 12.5 }, children: [_jsx(Box, { component: "span", sx: { width: 70, flexShrink: 0, color: "#667085", pt: "3px" }, children: "Recipients" }), _jsx(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 0.5, minWidth: 0 }, children: schedule.recipients.map((r) => (_jsx(HoverTooltip, { title: RECIPIENT_KIND[r.kind], placement: "top", children: _jsx(Box, { component: "span", sx: {
                                            px: 1,
                                            py: "2px",
                                            borderRadius: "999px",
                                            bgcolor: r.kind === "internal" ? "#F2F4F7" : "#FFF1F8",
                                            color: r.kind === "internal" ? "#344054" : BRAND,
                                            fontSize: 12,
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                        }, children: r.name }) }, `${r.kind}.${r.id}`))) })] }), schedule.replaces_existing && !done && (_jsx(Typography, { sx: { fontSize: 12, color: "#B54708" }, children: "This replaces the report's current schedule." }))] }))) : (_jsx(Typography, { sx: { fontSize: 12.5, color: "#475467" }, children: "This schedule could not be read." }))) : isClone ? (done ? (_jsxs(Typography, { sx: { fontSize: 12.5, color: "#475467" }, children: ["Cloned as ", _jsxs("b", { children: ["\"", name, "\""] }), cloneClientName ? _jsxs(_Fragment, { children: [" for ", _jsx("b", { children: cloneClientName })] }) : null, "."] })) : (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.25, "& .form-input-wrapper": { width: "100%" } }, children: [_jsx(TextInput, { label: "New report name", required: true, value: name, onChange: (e) => {
                            setName(e.target.value);
                            setNameError("");
                        }, placeholder: "Enter report name", fullWidth: true, disabled: running || !isLatest, error: Boolean(nameError), errorMessage: nameError || undefined }), _jsx(Dropdown, { label: "Client", required: true, value: cloneClientId, options: options, onChange: (value) => setCloneClientId(String(value ?? "")), placeholder: "Select Client", searchable: true, fullWidth: true, usePortal: true, disabled: running || !isLatest }), networkCheck?.allMissing ? (_jsxs(Typography, { sx: { fontSize: 12, color: "#B42318" }, children: ["No active account is connected for this client on any of the report's networks (", networkCheck.missing.join(", "), "), so the report cannot be cloned."] })) : networkCheck && networkCheck.missing.length > 0 ? (_jsxs(Typography, { sx: { fontSize: 12, color: "#B54708" }, children: ["No active account is connected for this client on ", networkCheck.missing.join(", "), ". The copy will be created without the metrics of ", networkCheck.missing.length > 1 ? "these networks" : "this network", "."] })) : null, action.client_name && !requestedClient && (_jsxs(Typography, { sx: { fontSize: 12, color: "#B54708" }, children: ["No client matched \"", action.client_name, "\" - pick one above."] }))] }))) : (_jsxs(Typography, { sx: { fontSize: 12.5, color: "#475467", lineHeight: "18px" }, children: [done ? "Sent. " : "", "\"", ctx?.report_name || "This report", "\" ", done ? "was" : "will be", " emailed to all active contacts of", " ", _jsx("b", { children: ctx?.client_name || "this client" }), " who have email notifications enabled."] })), _jsx(Box, { sx: { display: "flex", justifyContent: "flex-end" }, children: done ? (_jsxs(Typography, { sx: { fontSize: 12.5, fontWeight: 600, color: "#027A48", display: "inline-flex", alignItems: "center", gap: 0.5 }, children: [_jsx(Check, { size: 14 }), " ", doneLabel] })) : (_jsx(HoverTooltip, { title: unavailable ? "Not available for this report" : isLatest ? "" : "Only the latest request can be run", placement: "top", children: _jsx("span", { children: _jsx(AppButton, { variant: "contained", size: "small", loading: running, disabled: unavailable || !isLatest || busy || (isClone && (!name.trim() || !cloneClientId || Boolean(networkCheck?.allMissing))), onClick: run, startIcon: icon(14), sx: {
                                height: 32,
                                px: 1.5,
                                bgcolor: BRAND,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: 12.5,
                                whiteSpace: "nowrap",
                                boxShadow: "0 6px 14px -6px rgba(255,0,142,0.55)",
                                "&:hover": { bgcolor: "#E6007A" },
                            }, children: buttonLabel }) }) })) })] }));
};
const AskAiDrawer = ({ open, onClose, clientId, clientName, availableNetworks, state, onApply, reportActions }) => {
    const token = useSelector(selectToken) || "";
    const user = useSelector(selectUser);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    /** The reply as it streams in; cleared when the turn commits. */
    const [streaming, setStreaming] = useState("");
    const [step, setStep] = useState(0);
    const inputRef = useRef(null);
    const scrollRef = useRef(null);
    const abortRef = useRef(null);
    const stateRef = useRef(state);
    stateRef.current = state;
    // A saved report also offers its actions, so Clone / Send are discoverable.
    const reportCtx = reportActions?.context;
    const starters = useMemo(() => {
        const out = startersFor(availableNetworks).slice(0, reportCtx?.saved ? 3 : 4);
        if (reportCtx?.saved && reportCtx.can_clone)
            out.push("Clone this report");
        if (reportCtx?.saved && reportCtx.can_send)
            out.push("Send this report to the client");
        return out;
    }, [availableNetworks, reportCtx?.saved, reportCtx?.can_clone, reportCtx?.can_send]);
    const period = fmtDateRange(state.period_start, state.period_end);
    // ── Conversations (ai_chats) ─────────────────────────────────────────
    /** The open conversation; null until its first turn is saved. */
    const [conversationId, setConversationId] = useState(null);
    const [conversation, setConversation] = useState(null);
    const [loadingConversation, setLoadingConversation] = useState(false);
    const [view, setView] = useState("chat");
    const [conversations, setConversations] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historySearch, setHistorySearch] = useState("");
    const [renamingId, setRenamingId] = useState(null);
    const [renameDraft, setRenameDraft] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    /** The saved report this drawer plans; null on Add (chats are drafts of the client). */
    const reportId = reportActions?.reportId ?? null;
    const activeKey = activeConversationKey(String(user?.id ?? user?._id ?? ""), reportId, clientId);
    /** Resume the last conversation once per mount, not on every reopen. */
    const resumedRef = useRef(false);
    /**
     * The scope changed under the drawer (a different client picked on Add):
     * the open chat belongs to the old scope, so drop it and resume this
     * scope's own last chat next time the drawer is open.
     */
    const scopeRef = useRef(activeKey);
    useEffect(() => {
        if (scopeRef.current === activeKey)
            return;
        scopeRef.current = activeKey;
        abortRef.current?.abort();
        setSending(false);
        setStreaming("");
        setConversationId(null);
        setConversation(null);
        setMessages([]);
        setConversations([]);
        setView("chat");
        resumedRef.current = false;
        if (open) {
            resumedRef.current = true;
            const last = readActive(activeKey);
            if (last)
                openConversation(last, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeKey]);
    const openConversation = async (id, quiet = false) => {
        abortRef.current?.abort();
        setSending(false);
        setStreaming("");
        setView("chat");
        setLoadingConversation(true);
        try {
            const res = await reportBuilderChatService.get(id, token);
            if (!res?.data?.success)
                throw new Error(res?.data?.message || res?.response?.data?.message);
            const data = res.data.data;
            // Only this report's own chats open here (a draft only on its own
            // client's Add page) - a plan written for another report must never
            // be applied to this one.
            const belongs = (data.summary?.report_id ?? null) === reportId &&
                (reportId !== null || String(data.summary?.client_id) === String(clientId));
            if (!belongs)
                throw new Error("That chat belongs to a different report.");
            setConversationId(id);
            setConversation(data.summary);
            setMessages((data.messages || []).map(fromStored));
            writeActive(activeKey, id);
        }
        catch (err) {
            // Deleted elsewhere, or not this user's: fall back to a new chat.
            writeActive(activeKey, null);
            setConversationId(null);
            setConversation(null);
            setMessages([]);
            if (!quiet)
                toast.error(err?.message || "Could not open that conversation.");
        }
        finally {
            setLoadingConversation(false);
        }
    };
    const newChat = () => {
        abortRef.current?.abort();
        setSending(false);
        setStreaming("");
        setConversationId(null);
        setConversation(null);
        setMessages([]);
        setDraft("");
        setView("chat");
        writeActive(activeKey, null);
        window.setTimeout(() => inputRef.current?.focus(), 50);
    };
    const loadConversations = async () => {
        setHistoryLoading(true);
        try {
            // This report's chats only - or, on Add, this client's drafts.
            const res = await reportBuilderChatService.list(reportId ? { report_id: reportId } : { client_id: clientId }, token);
            if (!res?.data?.success)
                throw new Error(res?.data?.message || res?.response?.data?.message);
            setConversations(res.data.data || []);
        }
        catch (err) {
            toast.error(err?.message || "Could not load your conversations.");
        }
        finally {
            setHistoryLoading(false);
        }
    };
    const showHistory = () => {
        setView("history");
        setHistorySearch("");
        setRenamingId(null);
        loadConversations();
    };
    const saveRename = async (item) => {
        const title = renameDraft.trim();
        setRenamingId(null);
        if (!title || title === item.title)
            return;
        setConversations((prev) => prev.map((c) => (c.id === item.id ? { ...c, title } : c)));
        if (conversation?.id === item.id)
            setConversation({ ...conversation, title });
        try {
            const res = await reportBuilderChatService.rename(item.id, title, token);
            if (!res?.data?.success)
                throw new Error(res?.data?.message || res?.response?.data?.message);
        }
        catch (err) {
            toast.error(err?.message || "Could not rename the conversation.");
            loadConversations();
        }
    };
    const confirmDelete = async () => {
        if (!deleteTarget)
            return;
        setDeleting(true);
        try {
            const res = await reportBuilderChatService.remove(deleteTarget.id, token);
            if (!res?.data?.success)
                throw new Error(res?.data?.message || res?.response?.data?.message);
            setConversations((prev) => prev.filter((c) => c.id !== deleteTarget.id));
            if (deleteTarget.id === conversationId) {
                setConversationId(null);
                setConversation(null);
                setMessages([]);
                writeActive(activeKey, null);
            }
            toast.success("Conversation deleted.");
            setDeleteTarget(null);
        }
        catch (err) {
            toast.error(err?.message || "Could not delete the conversation.");
        }
        finally {
            setDeleting(false);
        }
    };
    useEffect(() => {
        if (!open) {
            // Closing cancels a turn still being written; it is not saved.
            abortRef.current?.abort();
            return;
        }
        setDraft("");
        if (!resumedRef.current) {
            resumedRef.current = true;
            const last = readActive(activeKey);
            if (last && !conversationId)
                openConversation(last, true);
        }
        const id = window.setTimeout(() => inputRef.current?.focus(), 250);
        return () => window.clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // Keep the newest message in view.
    useEffect(() => {
        const el = scrollRef.current;
        if (el)
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }, [messages, streaming, sending]);
    // Thinking steps before the first token arrives.
    useEffect(() => {
        if (!sending || streaming)
            return;
        setStep(0);
        const tick = window.setInterval(() => setStep((s) => Math.min(s + 1, THINK_STEPS.length - 1)), 1400);
        return () => window.clearInterval(tick);
    }, [sending, streaming]);
    /** The builder's current selection in API vocabulary, for the request. */
    const currentSelection = () => {
        const s = stateRef.current;
        return {
            report_name: s.report_name,
            networks: s.network_keys.map((key) => ({
                network: toApiNetworkKey(key),
                columns: s.column_order.filter((id) => id.startsWith(`${key}.`)).map((id) => id.slice(key.length + 1)),
                source: s.source_by_network?.[key] ?? null,
                breakdowns: s.breakdowns_by_network?.[key] || [],
                // Filters too, so a plan re-proposed for a follow-up keeps them.
                enum_filters: Object.entries(s.enum_filters_by_network?.[key] || {})
                    .filter(([, values]) => values.length)
                    .map(([field, values]) => ({ field, values })),
                filters: (s.filters_by_network?.[key] || [])
                    .filter((f) => f.values[0]?.trim())
                    .map((f) => ({ field: f.field_id, operator: f.operator, value: f.values[0].trim() })),
            })),
            dimensions: s.dimension_ids,
            // Formula columns too: "add CTR" must keep them, "change Blended ROAS" can edit them.
            custom_metrics: (s.custom_metrics || []).map((m) => ({
                key: m.key,
                name: m.name,
                description: m.description || "",
                format: m.format,
                position: m.position,
                formula: m.formula,
            })),
        };
    };
    const send = async (text) => {
        const value = text.trim();
        if (!value || sending)
            return;
        // Optimistic until the server stores the turn and hands back its ids.
        const userMsg = { id: uid(), role: "user", content: value };
        setMessages((prev) => [...prev, userMsg]);
        setDraft("");
        setSending(true);
        setStreaming("");
        const abort = new AbortController();
        abortRef.current = abort;
        try {
            const turn = await sendReportBuilderMessage({
                clientId,
                message: value,
                conversationId,
                reportId,
                report: reportActions?.context ?? null,
                selection: currentSelection(),
                // Compare included: "compare with previous month" is worked out from it.
                period: {
                    start: stateRef.current.period_start,
                    end: stateRef.current.period_end,
                    compare_start: stateRef.current.compare_start,
                    compare_end: stateRef.current.compare_end,
                },
            }, token, abort.signal, (delta) => setStreaming((prev) => prev + delta));
            // Swap the optimistic message for the stored one, then add the reply.
            setMessages((prev) => [
                ...prev.map((m) => (m.id === userMsg.id ? fromStored(turn.user_message) : m)),
                fromStored(turn.message),
            ]);
            if (turn.conversation_id !== conversationId) {
                setConversationId(turn.conversation_id);
                writeActive(activeKey, turn.conversation_id);
            }
            setConversation((prev) => prev && prev.id === turn.conversation_id
                ? prev
                : {
                    id: turn.conversation_id,
                    title: value.length > 80 ? `${value.slice(0, 79)}…` : value,
                    client_id: clientId,
                    client_name: clientName || null,
                    // The scope the server just stored it under.
                    report_id: reportId,
                    message_count: 2,
                    last_message_at: new Date().toISOString(),
                    created_at: new Date().toISOString(),
                });
        }
        catch (err) {
            if (abort.signal.aborted)
                return;
            // The server refused: this chat belongs to another report. Never
            // retry it here - start this report's own chat instead.
            if (/different report/i.test(String(err?.message || ""))) {
                toast.error(err.message);
                newChat();
                return;
            }
            setMessages((prev) => [
                ...prev,
                { id: uid(), role: "assistant", content: "", error: err?.message || "Could not reach the AI agent. Please try again." },
            ]);
        }
        finally {
            if (!abort.signal.aborted) {
                setSending(false);
                setStreaming("");
                window.setTimeout(() => inputRef.current?.focus(), 50);
            }
        }
    };
    /** Retry: resend the user message right before the failed turn. */
    const retry = (failedId) => {
        const index = messages.findIndex((m) => m.id === failedId);
        const previous = [...messages.slice(0, index)].reverse().find((m) => m.role === "user");
        if (!previous)
            return;
        setMessages((prev) => prev.filter((m) => m.id !== failedId && m.id !== previous.id));
        window.setTimeout(() => send(previous.content), 0);
    };
    const apply = (message) => {
        if (!message.plan)
            return;
        onApply(message.plan);
        setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, applied: true } : m)));
        // Remembered on the server so the card still reads "Applied" next time.
        if (conversationId && message.saved) {
            reportBuilderChatService.markApplied(conversationId, message.id, token).catch(() => undefined);
        }
    };
    /**
         * Run a confirmed Clone / Send and record it on the message. A clone opens
         * the copy on Edit, so the drawer closes to let it load; the conversation
         * resumes there because its id is remembered.
         */
    const runAction = async (message, execute) => {
        const ok = await execute();
        if (!ok)
            return;
        setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, applied: true } : m)));
        if (conversationId && message.saved) {
            reportBuilderChatService.markApplied(conversationId, message.id, token).catch(() => undefined);
        }
        if (message.action?.type === "clone")
            onClose();
    };
    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send(draft);
        }
    };
    const canSend = Boolean(draft.trim()) && !sending && availableNetworks.length > 0;
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant" && !m.error);
    // ── Renderers ────────────────────────────────────────────────────────
    const labelFor = (apiNetwork) => {
        const key = toBuilderNetworkKey(apiNetwork);
        return { key, label: availableNetworks.find((n) => n.key === key)?.label || apiNetwork, color: getNetwork(key)?.color };
    };
    const metricLabel = (apiNetwork, metric) => {
        const key = toBuilderNetworkKey(apiNetwork);
        return availableNetworks.find((n) => n.key === key)?.metrics.find((m) => m.key === metric)?.label || metric;
    };
    /**
     * A formula in words for the card: "{meta.spend}" → "Meta Spend", a custom
     * key → that metric's name. The stored formula keeps its keys.
     */
    const readableFormula = (formula, customs) => formula.replace(/\{([^}]+)\}/g, (_match, ref) => {
        const custom = customs.find((c) => c.key === ref.trim());
        if (custom)
            return custom.name;
        const [net, ...rest] = ref.trim().split(".");
        if (!rest.length)
            return ref;
        const network = availableNetworks.find((n) => n.key === toBuilderNetworkKey(net));
        const metric = network?.metrics.find((m) => m.key === rest.join("."));
        return metric?.columnLabel || metric?.label || ref;
    });
    /** Applying this plan would move the page's date picker (range or compare). */
    const datesChange = (plan) => {
        if (!plan.period)
            return false;
        return (plan.period.start !== state.period_start ||
            plan.period.end !== state.period_end ||
            (plan.compare?.start ?? null) !== (state.compare_start ?? null) ||
            (plan.compare?.end ?? null) !== (state.compare_end ?? null));
    };
    const planCard = (message) => {
        const plan = message.plan;
        const applied = Boolean(message.applied);
        // Only the newest plan can be applied: an older one no longer reflects the chat.
        const isLatest = lastAssistant?.id === message.id;
        let chipIndex = 0;
        return (_jsxs(Box, { sx: {
                position: "relative",
                bgcolor: "#FCFCFD",
                border: "1px solid",
                borderColor: applied ? "#A6F4C5" : "#EAECF0",
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                boxShadow: "0 8px 24px -16px rgba(16,24,40,0.25)",
                transition: "border-color .3s ease",
                ...rise(1, 80),
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 14,
                    right: 14,
                    height: 2,
                    borderRadius: 1,
                    background: applied ? "linear-gradient(135deg, #12B76A, #32D583)" : BRAND_GRADIENT,
                },
            }, children: [plan.report_name && (_jsx(PlanRow, { label: "Name", index: 1, children: _jsx(Typography, { sx: { fontSize: 13, fontWeight: 600, color: "#101828", pt: "3px" }, children: plan.report_name }) })), _jsx(PlanRow, { label: "Networks", index: 2, children: plan.networks.map((n) => {
                        const info = labelFor(n.network);
                        return (_jsxs(Chip, { index: chipIndex++, icon: _jsx(GroupIcon, { group: info.key, size: 13 }), color: info.color, children: [info.label, n.source ? ` · ${n.source}` : ""] }, n.network));
                    }) }), _jsx(PlanRow, { label: "Columns", index: 3, children: plan.networks.flatMap((n) => n.columns.map((metric) => (_jsx(Chip, { index: chipIndex++, icon: _jsx(GroupIcon, { group: labelFor(n.network).key, size: 11 }), children: metricLabel(n.network, metric) }, `${n.network}.${metric}`)))) }), _jsxs(PlanRow, { label: "Breakdown", index: 4, children: [plan.dimensions.map((d) => (_jsx(Chip, { index: chipIndex++, icon: _jsx(CalendarDays, { size: 12 }), children: DIMENSION_LABELS[d] || d }, d))), plan.networks.some((n) => n.breakdowns.length) && (_jsxs(Typography, { sx: { fontSize: 11.5, color: "#98A2B3", pt: "4px" }, children: ["+ ", plan.networks.flatMap((n) => n.breakdowns).join(", ")] }))] }), (plan.custom_metrics?.length ?? 0) > 0 && (_jsx(PlanRow, { label: "Custom", index: 5, children: _jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.6, minWidth: 0 }, children: plan.custom_metrics.map((metric) => (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.6, flexWrap: "wrap" }, children: [_jsx(Chip, { index: chipIndex++, icon: _jsx(Sigma, { size: 12 }), color: BRAND, children: metric.name }), _jsx(Typography, { sx: { fontSize: 11, color: "#98A2B3" }, children: metric.format === "currency" ? "Currency" : metric.format === "percentage" ? "Percent" : "Number" })] }), _jsxs(Typography, { sx: {
                                        fontSize: 11.5,
                                        color: "#667085",
                                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                        wordBreak: "break-word",
                                        pl: 0.25,
                                    }, children: ["= ", readableFormula(metric.formula, plan.custom_metrics)] })] }, metric.key))) }) })), plan.networks.some((n) => n.filters.length || n.enum_filters.length) && (_jsx(PlanRow, { label: "Filters", index: 5, children: plan.networks.flatMap((n) => [
                        ...n.enum_filters.map((f) => (_jsxs(Chip, { index: chipIndex++, children: [f.field, ": ", f.values.join(", ")] }, `${n.network}.${f.field}`))),
                        ...n.filters.map((f, i) => (_jsxs(Chip, { index: chipIndex++, children: [f.field, " ", f.operator === "contains" ? "contains" : "excludes", " \"", f.value, "\""] }, `${n.network}.${f.field}.${i}`))),
                    ]) })), _jsxs(PlanRow, { label: "Period", index: 6, children: [_jsx(Chip, { index: chipIndex++, icon: _jsx(CalendarDays, { size: 12 }), children: plan.period ? fmtDateRange(plan.period.start, plan.period.end) : period }), plan.compare ? (_jsxs(Chip, { index: chipIndex++, children: ["vs ", fmtDateRange(plan.compare.start, plan.compare.end)] })) : (_jsx(Chip, { index: chipIndex++, color: "#98A2B3", children: "No comparison" })), _jsx(Typography, { sx: { fontSize: 11.5, color: datesChange(plan) ? BRAND : "#98A2B3", pt: "4px" }, children: datesChange(plan) ? "updates the date picker" : "same as the date picker" })] }), plan.notes.length > 0 && (_jsx(Box, { component: "ul", sx: { m: 0, pl: 2, display: "flex", flexDirection: "column", gap: 0.25 }, children: plan.notes.map((note, i) => (_jsx(Typography, { component: "li", sx: { fontSize: 12, color: "#667085", lineHeight: "17px", ...rise(6 + i, 60) }, children: note }, i))) })), _jsx(Box, { sx: { display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mt: 0.5, ...rise(7, 60) }, children: applied ? (_jsxs(Typography, { sx: { fontSize: 12.5, fontWeight: 600, color: "#027A48", display: "inline-flex", alignItems: "center", gap: 0.5 }, children: [_jsx(Check, { size: 14 }), " Applied - report generated"] })) : (_jsx(HoverTooltip, { title: isLatest ? "" : "Only the latest plan can be applied", placement: "top", children: _jsx("span", { children: _jsx(AppButton, { variant: "contained", size: "small", disabled: !isLatest || sending || plan.networks.length === 0, onClick: () => apply(message), startIcon: _jsx(Check, { size: 14 }), sx: {
                                    position: "relative",
                                    overflow: "hidden",
                                    height: 32,
                                    px: 1.5,
                                    bgcolor: "#FF008E",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: 12.5,
                                    whiteSpace: "nowrap",
                                    boxShadow: "0 6px 14px -6px rgba(255,0,142,0.55)",
                                    "&:hover": { bgcolor: "#E6007A", transform: "translateY(-1px)" },
                                    "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        bottom: 0,
                                        left: 0,
                                        width: "40%",
                                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
                                        animation: "askAiSweep 1.1s ease-out 700ms both",
                                        [REDUCED]: { display: "none" },
                                    },
                                }, children: "Apply & Generate Report" }) }) })) })] }));
    };
    const assistantBubble = (message) => (_jsxs(Box, { sx: { display: "flex", gap: 1.25, alignItems: "flex-start", ...rise(0) }, children: [_jsx(AiAvatar, { done: message.applied }), _jsx(Box, { sx: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }, children: message.error ? (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, bgcolor: "#FFFBFA", border: "1px solid #FECDCA", borderRadius: "4px 14px 14px 14px", px: 1.5, py: 1 }, children: [_jsx(Typography, { sx: { flex: 1, fontSize: 13, color: "#B42318" }, children: message.error }), _jsx(AppButton, { variant: "text", size: "small", onClick: () => retry(message.id), startIcon: _jsx(RotateCcw, { size: 13 }), sx: { textTransform: "none", fontWeight: 600, color: "#B42318", minWidth: 0, px: 1 }, children: "Retry" })] })) : (_jsxs(_Fragment, { children: [message.content && (_jsx(Box, { sx: { alignSelf: "flex-start", maxWidth: "100%", bgcolor: "#fff", border: "1px solid #EAECF0", borderRadius: "4px 14px 14px 14px", px: 1.5, py: 1, "& p, & li": { fontSize: "13.5px" } }, children: _jsx(AiRichText, { text: message.content }) })), message.plan && message.plan.networks.length > 0 && planCard(message), message.action && (_jsx(ActionCard, { action: message.action, done: Boolean(message.applied), isLatest: lastAssistant?.id === message.id, actions: reportActions, busy: sending, onRun: (execute) => runAction(message, execute) })), message.options && message.options.length > 0 && (_jsx(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.25 }, children: message.options.map((o, i) => (_jsx(OptionChip, { label: o, index: i, disabled: sending || lastAssistant?.id !== message.id, onPick: send }, o))) }))] })) })] }, message.id));
    const userBubble = (message) => (_jsx(Box, { sx: { display: "flex", justifyContent: "flex-end", ...rise(0) }, children: _jsx(Box, { sx: { maxWidth: "82%", background: "linear-gradient(135deg, #FFF3FA 0%, #FFFAFD 100%)", border: "1px solid #FFC8E9", borderRadius: "14px 14px 4px 14px", px: 1.5, py: 1, boxShadow: "0 2px 8px -4px rgba(255,0,142,0.25)" }, children: _jsx(Typography, { sx: { fontSize: 13.5, color: "#101828", lineHeight: "20px", whiteSpace: "pre-wrap", wordBreak: "break-word" }, children: message.content }) }) }, message.id));
    /** The agent's opening, before any AI spend: a greeting and the starters. */
    const greeting = (_jsxs(Box, { sx: { display: "flex", gap: 1.25, alignItems: "flex-start", ...rise(0) }, children: [_jsx(AiAvatar, {}), _jsxs(Box, { sx: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }, children: [_jsx(Box, { sx: { alignSelf: "flex-start", bgcolor: "#fff", border: "1px solid #EAECF0", borderRadius: "4px 14px 14px 14px", px: 1.5, py: 1 }, children: _jsxs(Typography, { sx: { fontSize: 13.5, color: "#101828", lineHeight: "20px" }, children: ["Hi! Tell me what you want to see", clientName && clientName !== "-" ? ` for ${clientName}` : "", " and I will set the report up. Name the networks and metrics, or just describe the goal."] }) }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.6, ...rise(1, 80) }, children: [_jsx(Typography, { sx: { fontSize: 11.5, color: "#98A2B3", mr: 0.25 }, children: "Connected:" }), availableNetworks.length ? (availableNetworks.map((n, i) => (_jsx(Chip, { index: i, icon: _jsx(GroupIcon, { group: n.key, size: 12 }), color: getNetwork(n.key)?.color, children: n.label }, n.key)))) : (_jsx(Typography, { sx: { fontSize: 12.5, color: "#B54708" }, children: "Select a client first - no networks to read from." }))] }), starters.length > 0 && (_jsx(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.25 }, children: starters.map((s, i) => (_jsx(OptionChip, { label: s, index: 2 + i, disabled: sending, onPick: send }, s))) }))] })] }));
    const thinkingBubble = (_jsxs(Box, { sx: { display: "flex", gap: 1.25, alignItems: "flex-start", ...rise(0) }, children: [_jsx(AiAvatar, { busy: true }), _jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: streaming ? (_jsxs(Box, { sx: { alignSelf: "flex-start", bgcolor: "#fff", border: "1px solid #EAECF0", borderRadius: "4px 14px 14px 14px", px: 1.5, py: 1, "& p, & li": { fontSize: "13.5px" } }, children: [_jsx(AiRichText, { text: streaming }), _jsx(Box, { sx: { display: "inline-block", width: 2, height: 13, mt: "2px", bgcolor: BRAND, verticalAlign: "text-bottom", animation: "askAiCaret 1s step-end infinite", [REDUCED]: { animation: "none" } } })] })) : (_jsxs(Box, { sx: { display: "inline-flex", alignItems: "center", gap: 1, bgcolor: "#fff", border: "1px solid #EAECF0", borderRadius: "4px 14px 14px 14px", px: 1.5, py: 1 }, children: [_jsx(Typography, { sx: { fontSize: 13, color: "#667085", ...rise(0) }, children: THINK_STEPS[step] }, step), _jsx(Box, { sx: { display: "flex", gap: "3px" }, children: [0, 1, 2].map((i) => (_jsx(Box, { sx: { width: 4, height: 4, borderRadius: "50%", bgcolor: BRAND, animation: "askAiDot 1.2s ease-in-out infinite", animationDelay: `${i * 0.16}s`, [REDUCED]: { animation: "none", opacity: 0.5 } } }, i))) })] })) })] }));
    // ── History view ─────────────────────────────────────────────────────
    const filteredConversations = conversations.filter((c) => {
        const q = historySearch.trim().toLowerCase();
        return !q || c.title.toLowerCase().includes(q) || (c.client_name || "").toLowerCase().includes(q);
    });
    const sections = [];
    for (const item of filteredConversations) {
        const label = sectionOf(item.last_message_at);
        const section = sections.find((s) => s.label === label);
        if (section)
            section.items.push(item);
        else
            sections.push({ label, items: [item] });
    }
    const historyRow = (item, index) => {
        const active = item.id === conversationId;
        const renaming = renamingId === item.id;
        return (_jsxs(Box, { role: "button", tabIndex: 0, onClick: () => !renaming && openConversation(item.id), onKeyDown: (e) => {
                if (!renaming && (e.key === "Enter" || e.key === " "))
                    openConversation(item.id);
            }, sx: {
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1.25,
                py: 1,
                borderRadius: "10px",
                cursor: renaming ? "default" : "pointer",
                border: "1px solid",
                borderColor: active ? "#FFC8E9" : "transparent",
                bgcolor: active ? "#FFFAFD" : "transparent",
                transition: "background-color .15s ease, border-color .15s ease",
                "&:hover": { bgcolor: active ? "#FFF3FA" : "#F9FAFB" },
                "& .row-actions": { opacity: 0, transition: "opacity .15s ease" },
                "&:hover .row-actions, &:focus-within .row-actions": { opacity: 1 },
                ...rise(index, 30),
            }, children: [_jsx(Box, { sx: {
                        width: 30,
                        height: 30,
                        borderRadius: "8px",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: active ? "#FFE4F3" : "#F2F4F7",
                        color: active ? BRAND : "#667085",
                    }, children: _jsx(MessageSquare, { size: 15 }) }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [renaming ? (_jsx(Box, { component: "input", autoFocus: true, value: renameDraft, maxLength: 80, onClick: (e) => e.stopPropagation(), onChange: (e) => setRenameDraft(e.target.value), onKeyDown: (e) => {
                                e.stopPropagation();
                                if (e.key === "Enter")
                                    saveRename(item);
                                if (e.key === "Escape")
                                    setRenamingId(null);
                            }, onBlur: () => saveRename(item), sx: {
                                width: "100%",
                                fontFamily: "inherit",
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#101828",
                                border: `1px solid ${BRAND}`,
                                borderRadius: "6px",
                                px: 0.75,
                                py: 0.25,
                                outline: "none",
                                boxShadow: "0 0 0 3px rgba(255,0,142,0.12)",
                            } })) : (_jsx(Typography, { sx: { fontSize: 13, fontWeight: 600, color: "#101828", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: item.title })), _jsx(Typography, { sx: { fontSize: 11.5, color: "#98A2B3", mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: [item.client_name, relativeTime(item.last_message_at), `${Math.ceil(item.message_count / 2)} ${item.message_count > 2 ? "turns" : "turn"}`]
                                .filter(Boolean)
                                .join(" · ") })] }), !renaming && (_jsxs(Box, { className: "row-actions", sx: { display: "flex", gap: 0.25 }, children: [_jsx(HoverTooltip, { title: "Rename", placement: "top", children: _jsx(IconButton, { size: "small", "aria-label": "Rename conversation", onClick: (e) => {
                                    e.stopPropagation();
                                    setRenamingId(item.id);
                                    setRenameDraft(item.title);
                                }, sx: { "&:hover": { bgcolor: "#FFF3FA", color: BRAND } }, children: _jsx(Pencil, { size: 14 }) }) }), _jsx(HoverTooltip, { title: "Delete", placement: "top", children: _jsx(IconButton, { size: "small", "aria-label": "Delete conversation", onClick: (e) => {
                                    e.stopPropagation();
                                    setDeleteTarget(item);
                                }, sx: { "&:hover": { bgcolor: "#FEF3F2", color: "#D92D20" } }, children: _jsx(Trash2, { size: 14 }) }) })] }))] }, item.id));
    };
    const historyPanel = (_jsxs(Box, { sx: { position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: "#FCFCFD" }, children: [_jsxs(Box, { sx: { px: 2.5, pt: 2, pb: 1.25, display: "flex", gap: 1, alignItems: "center", ...rise(0) }, children: [_jsxs(Box, { sx: {
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            height: 36,
                            px: 1.25,
                            bgcolor: "#fff",
                            border: "1px solid #D0D5DD",
                            borderRadius: "8px",
                            "&:focus-within": { borderColor: BRAND, boxShadow: "0 0 0 3px rgba(255,0,142,0.12)" },
                        }, children: [_jsx(Search, { size: 15, color: "#98A2B3" }), _jsx(Box, { component: "input", value: historySearch, onChange: (e) => setHistorySearch(e.target.value), placeholder: "Search your chats", sx: { flex: 1, border: "none", outline: "none", fontFamily: "inherit", fontSize: 13, color: "#101828", bgcolor: "transparent", "&::placeholder": { color: "#98A2B3" } } })] }), _jsx(AppButton, { variant: "contained", onClick: newChat, startIcon: _jsx(SquarePen, { size: 14 }), sx: { height: 36, px: 1.5, bgcolor: BRAND, textTransform: "none", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", boxShadow: "none", "&:hover": { bgcolor: "#E6007A" } }, children: "New chat" })] }), _jsx(Box, { sx: { flex: 1, minHeight: 0, overflowY: "auto", px: 1.5, pb: 2 }, children: historyLoading ? (_jsx(Box, { sx: { display: "flex", justifyContent: "center", py: 6 }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : !conversations.length ? (_jsxs(Box, { sx: { textAlign: "center", py: 6, px: 3, ...rise(1) }, children: [_jsx(Box, { sx: { display: "inline-flex", mb: 1.5 }, children: _jsx(AiAvatar, { size: 40 }) }), _jsx(Typography, { sx: { fontSize: 14, fontWeight: 600, color: "#344054" }, children: "No conversations yet" }), _jsx(Typography, { sx: { fontSize: 12.5, color: "#667085", mt: 0.5 }, children: reportId
                                ? "Chats you have with the G8A AI Agent about this report are saved here until you delete them."
                                : "Chats started on a new report are saved here, and move to that report once it is saved." })] })) : !filteredConversations.length ? (_jsxs(Typography, { sx: { fontSize: 13, color: "#667085", textAlign: "center", py: 4 }, children: ["No chats match \"", historySearch.trim(), "\"."] })) : (sections.map((section) => (_jsxs(Box, { sx: { mt: 1 }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: "#98A2B3", textTransform: "uppercase", letterSpacing: 0.4, px: 1.25, py: 0.75 }, children: section.label }), _jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.25 }, children: section.items.map((item, i) => historyRow(item, i)) })] }, section.label)))) })] }));
    return (_jsxs(CommonDrawerLayout, { open: open, onClose: onClose, width: "md", title: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.25 }, children: [_jsx(AiAvatar, { size: 28, busy: sending }), _jsxs(Box, { children: [_jsx(Box, { component: "span", sx: { display: "block", lineHeight: "20px" }, children: "G8A AI Agent" }), _jsx(Typography, { sx: {
                                fontSize: 12,
                                fontWeight: 400,
                                color: "#667085",
                                lineHeight: "16px",
                                maxWidth: 330,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }, children: view === "history"
                                ? reportId
                                    ? "Chats for this report"
                                    : "Draft chats for this client"
                                : sending
                                    ? "Working on it"
                                    : conversation?.title || "Describe the report, review the plan, generate it" })] })] }), headerActions: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [_jsx(HoverTooltip, { title: view === "history" ? "Back to chat" : "Chat history", placement: "bottom", children: _jsx("span", { children: _jsx(IconButton, { size: "small", disabled: sending, onClick: () => (view === "history" ? setView("chat") : showHistory()), "aria-label": view === "history" ? "Back to chat" : "Chat history", sx: {
                                bgcolor: view === "history" ? "#FFF3FA" : "transparent",
                                "&:hover": { bgcolor: "#FFF3FA" },
                            }, children: view === "history" ? _jsx(ArrowLeft, { size: 16, color: BRAND }) : _jsx(History, { size: 16, color: "#71717A" }) }) }) }), _jsx(HoverTooltip, { title: "New chat", placement: "bottom", children: _jsx("span", { children: _jsx(IconButton, { size: "small", disabled: sending || (view === "chat" && !messages.length), onClick: newChat, "aria-label": "New chat", sx: { "&:hover": { bgcolor: "#FFF3FA" } }, children: _jsx(SquarePen, { size: 16, color: "#71717A" }) }) }) })] }), children: [_jsx(Box, { sx: {
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    overflow: "hidden",
                    ...KEYFRAMES,
                    // Soft brand-pink wash at the top, breathing slowly.
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: -140,
                        left: -80,
                        width: 380,
                        height: 280,
                        borderRadius: "50%",
                        background: "radial-gradient(closest-side, rgba(255,0,142,0.14), rgba(255,92,184,0.06) 60%, transparent)",
                        animation: "askAiGlow 5s ease-in-out infinite",
                        pointerEvents: "none",
                        zIndex: 0,
                        [REDUCED]: { animation: "none" },
                    },
                }, children: view === "history" ? (historyPanel) : (_jsxs(_Fragment, { children: [_jsx(Box, { ref: scrollRef, sx: { position: "relative", flex: 1, minHeight: 0, overflowY: "auto", px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2, bgcolor: "#FCFCFD" }, children: loadingConversation ? (_jsx(Box, { sx: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : (_jsxs(_Fragment, { children: [greeting, messages.map((m) => (m.role === "user" ? userBubble(m) : assistantBubble(m))), sending && thinkingBubble] })) }), _jsxs(Box, { sx: { position: "relative", px: 2.5, pt: 1.5, pb: 2, borderTop: "1px solid #F2F4F7", bgcolor: "#fff" }, children: [_jsx(Box, { sx: {
                                        borderRadius: "12px",
                                        p: "1.5px",
                                        background: "#D0D5DD",
                                        transition: "background .2s ease, box-shadow .2s ease",
                                        "&:focus-within": { background: BRAND_GRADIENT, boxShadow: "0 0 0 4px rgba(255,0,142,0.12)" },
                                    }, children: _jsxs(Box, { sx: { display: "flex", alignItems: "flex-end", gap: 1, borderRadius: "11px", px: 1.5, py: 1, bgcolor: "#fff" }, children: [_jsx(Box, { component: "textarea", ref: inputRef, value: draft, onChange: (e) => setDraft(e.target.value), onKeyDown: onKeyDown, disabled: sending || availableNetworks.length === 0, rows: 3, maxLength: 2000, placeholder: messages.length ? "Ask for a change, e.g. add CTR or break it down by week" : "e.g. Meta and Google Ads spend, clicks and ROAS by week", sx: { flex: 1, border: "none", outline: "none", resize: "none", fontFamily: "inherit", fontSize: "14px", lineHeight: "22px", color: "#101828", bgcolor: "transparent", maxHeight: 120, "&::placeholder": { color: "#98A2B3" } } }), _jsx(HoverTooltip, { title: canSend ? "Send (Enter)" : "", placement: "top", children: _jsx("span", { children: _jsx(IconButton, { size: "small", onClick: () => send(draft), disabled: !canSend, "aria-label": "Send", sx: {
                                                            width: 34,
                                                            height: 34,
                                                            background: canSend ? BRAND_GRADIENT : "#F2F4F7",
                                                            boxShadow: canSend ? "0 4px 12px rgba(255,0,142,0.35)" : "none",
                                                            transform: canSend ? "scale(1)" : "scale(0.92)",
                                                            transition: "transform .2s cubic-bezier(0.2,0.8,0.2,1), box-shadow .2s ease, background .2s ease",
                                                            "&:hover": { background: BRAND_GRADIENT, transform: "scale(1.06)" },
                                                            "&.Mui-disabled": { background: "#F2F4F7" },
                                                        }, children: _jsx(ArrowUp, { size: 16, color: canSend ? "#fff" : "#98A2B3" }) }) }) })] }) }), _jsx(Typography, { sx: { fontSize: 11, color: "#98A2B3", mt: 0.75 }, children: "Enter to send \u00B7 Shift+Enter for a new line. Apply & Generate runs and saves the report, like the Generate Report button." })] })] })) }), _jsx(ConfirmActionModal, { open: Boolean(deleteTarget), title: "Delete this conversation?", description: `"${deleteTarget?.title || ""}" and every message in it will be removed for good.`, confirmText: "Delete", cancelText: "Cancel", loading: deleting, onConfirm: confirmDelete, onClose: () => setDeleteTarget(null) })] }));
};
export default AskAiDrawer;
